<template>
  <div class="microbiology-timeline">
    <div
      ref="timelineScroll"
      class="timeline-scroll"
      @scroll="updateScrollState"
    >
      <div class="timeline-content">
        <div class="timeline-header">
          <div class="progress-track">
            <div
              class="progress-fill"
              :style="{ width: getProgressPercentage() + '%' }"
            ></div>
          </div>
        </div>

        <div class="timeline-steps">
          <div
            v-for="(step, index) in progressSteps"
            :key="index"
            class="timeline-step"
            :class="getStepClass(step)"
          >
            <div class="step-marker">
              <div class="step-circle"></div>
            </div>

            <div class="step-content">
              <div class="step-title">{{ step.step }}</div>
              <div class="box-date">
                <div class="step-year">{{ step.year }}</div>
                <div class="step-date">{{ step.date }}</div>
                <div class="step-time">{{ step.time }}</div>
              </div>
              <div class="step-description">{{ step.description }}</div>
              <div
                v-if="hasSubDescriptions(step)"
                class="step-subdescriptions"
              >
                <div
                  v-for="(subDescription, subDescriptionIndex) in step.subDescriptions"
                  :key="`subdescription-${subDescriptionIndex}`"
                  class="step-subdescription-item"
                >
                  <div
                    v-if="subDescription.text"
                    class="step-subdescription"
                  >
                    {{ subDescription.text }}
                  </div>
                  <div
                    v-if="subDescription.additionalInfo"
                    class="step-additional"
                  >
                    <button
                      v-if="hasPartialDetails(subDescription)"
                      class="step-additional-link"
                      @click.stop="openPartialWarningModal(subDescription)"
                    >
                      {{ subDescription.additionalInfo }}
                    </button>
                    <span v-else>
                      {{ subDescription.additionalInfo }}
                    </span>
                  </div>
                </div>
              </div>
              <div
                v-else-if="step.subDescription"
                class="step-subdescription"
              >
                {{ step.subDescription }}
              </div>
              <div
                v-if="step.additionalInfo"
                class="step-additional"
              >
                <button
                  v-if="hasPartialDetails(step)"
                  class="step-additional-link"
                  @click.stop="openPartialWarningModal(step)"
                >
                  {{ step.additionalInfo }}
                </button>
                <span v-else>
                  {{ step.additionalInfo }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="showArrows"
      class="timeline-navigation"
    >
      <button
        class="timeline-nav-btn"
        :disabled="!canScrollLeft"
        aria-label="Ver timeline para a esquerda"
        @click="scrollTimeline('left')"
      >
        <md-icon>arrow_back</md-icon>
      </button>
      <button
        class="timeline-nav-btn"
        :disabled="!canScrollRight"
        aria-label="Ver timeline para a direita"
        @click="scrollTimeline('right')"
      >
        <md-icon>arrow_forward</md-icon>
      </button>
    </div>

    <PartialWarningModal
      ref="partialWarningModal"
      @continue="openPartialDetailsModal"
    />

    <PartialDetailsModal
      ref="partialDetailsModal"
      :details="selectedPartialDetails"
    />
  </div>
</template>

<script>
import PartialWarningModal from './modals/PartialWarningModal.vue'
import PartialDetailsModal from './modals/PartialDetailsModal.vue'

export default {
  name: 'MicrobiologyProgressBar',
  components: {
    PartialWarningModal,
    PartialDetailsModal,
  },
  props: {
    progressSteps: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  data() {
    return {
      canScrollLeft: false,
      canScrollRight: false,
      showArrows: false,
      selectedPartialDetails: {
        microorganism: '',
        reportDate: '',
        unit: '',
        rows: [],
        footerNote: '',
        notes: [],
      },
    }
  },
  watch: {
    progressSteps: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.updateScrollState()
        })
      },
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.updateScrollState()
    })

    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleResize() {
      this.updateScrollState()
    },

    updateScrollState() {
      const scrollContainer = this.$refs.timelineScroll

      if (!scrollContainer) return

      const { scrollLeft, clientWidth, scrollWidth } = scrollContainer
      const maxScrollLeft = scrollWidth - clientWidth

      this.showArrows = maxScrollLeft > 0
      this.canScrollLeft = scrollLeft > 0
      this.canScrollRight = scrollLeft < maxScrollLeft - 1
    },

    scrollTimeline(direction) {
      const scrollContainer = this.$refs.timelineScroll

      if (!scrollContainer) return

      const scrollAmount = scrollContainer.clientWidth * 0.75
      const delta = direction === 'left' ? -scrollAmount : scrollAmount

      scrollContainer.scrollTo({
        left: scrollContainer.scrollLeft + delta,
        behavior: 'smooth',
      })
    },

    hasPartialDetails(step) {
      return !!(
        step &&
        step.partialAntibiogram &&
        Array.isArray(step.partialAntibiogram.rows)
      )
    },

    hasSubDescriptions(step) {
      return !!(
        step &&
        Array.isArray(step.subDescriptions) &&
        step.subDescriptions.length > 0
      )
    },

    openPartialWarningModal(step) {
      const details = step.partialAntibiogram || {}

      this.selectedPartialDetails = {
        microorganism: details.microorganism || '',
        reportDate: details.reportDate || '',
        unit: details.unit || '',
        rows: Array.isArray(details.rows) ? details.rows : [],
        footerNote: details.footerNote || '',
        notes: Array.isArray(details.notes) ? details.notes : [],
      }

      this.$refs.partialWarningModal.open()
    },

    openPartialDetailsModal() {
      this.$refs.partialWarningModal.close()
      this.$nextTick(() => {
        this.$refs.partialDetailsModal.open()
      })
    },

    closePartialDetailsModal() {
      this.$refs.partialDetailsModal.close()
    },

    getStepClass(step) {
      if (step.step.includes('Parcial')) return 'step-partial'
      if (step.status === 'completed') return 'step-completed'
      if (step.status === 'final') return 'step-final'
      return 'step-pending'
    },

    getProgressPercentage() {
      if (!this.progressSteps || this.progressSteps.length === 0) return 0

      const completedSteps = this.progressSteps.filter(
        step => step.status === 'completed' || step.status === 'final'
      ).length

      return Math.round((completedSteps / this.progressSteps.length) * 100)
    },
  },
}
</script>

<style lang="scss" scoped>
.microbiology-timeline {
  width: 100%;
  font-family: 'Exo 2', sans-serif;
  margin-top: 88px;

  .timeline-scroll {
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 82px 10px 10px;
    margin-top: -72px;
    scrollbar-width: thin;
    scrollbar-color: #c4c4c4 #f0f0f0;

    &::-webkit-scrollbar {
      height: 8px;
    }

    &::-webkit-scrollbar-track {
      background: #f0f0f0;
      border-radius: 99px;
    }

    &::-webkit-scrollbar-thumb {
      background: #c4c4c4;
      border-radius: 99px;
    }
  }

  .timeline-content {
    width: max-content;
    min-width: 100%;
  }

  .timeline-header {
    width: 100%;

    .progress-track {
      position: relative;
      height: 6px;
      background-color: #e6e6e6;
      border-radius: 3px;
      overflow: hidden;

      .progress-fill {
        height: 100%;
        background: linear-gradient(
          90deg,
          #ef9a4b 0%,
          #ee5c53 35%,
          #ec2072 70%,
          #7f1a54 100%
        );
        border-radius: 3px;
        transition: width 0.5s ease;
      }
    }
  }

  .timeline-steps {
    display: flex;
    position: relative;

    .timeline-step {
      flex: 1 0 180px;
      min-width: 180px;
      display: flex;
      align-items: flex-start;
      padding-right: 20px;
      position: relative;

      &:last-child {
        padding-right: 0;
      }

      .step-marker {
        position: absolute;
        top: -17px;
        left: -5px;
        border: 5px solid #ffffff;
        border-radius: 50%;

        .step-circle {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background-color: #e6e6e6;
          border: 3px solid #fff;
          box-shadow: 0 0 0 2px #e6e6e6;
          transition: all 0.3s ease;
        }
      }

      .step-content {
        flex: 1;
        padding-left: 16px;
        border-left: 2px solid #ccc;
        margin-left: 8px;
        margin-top: 13px;
        height: calc(100% - 12px);

        .box-date {
          position: absolute;
          top: -82px;
          left: -8px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;

          .step-year,
          .step-date,
          .step-time {
            color: #7a7a7a;
            font-weight: 700;
          }

          .step-year {
            font-size: 11px;
          }

          .step-date {
            font-size: 14px;
            margin-bottom: 2px;
          }

          .step-time {
            font-size: 12px;
            color: #363636;
            margin-bottom: 6px;
          }
        }

        .step-title {
          font-size: 12px;
          font-weight: 700;
          color: #303030;
          margin-bottom: 2px;
        }

        .step-description {
          font-size: 11px;
          color: #7a7a7a;
          text-transform: uppercase;
          margin-bottom: 2px;
        }

        .step-subdescriptions {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .step-subdescription-item {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .step-subdescription {
          font-size: 12px;
          color: #303030;
          margin-bottom: 2px;
        }

        .step-additional {
          font-size: 11px;
          color: #ec2072;

          .step-additional-link {
            border: none;
            background: transparent;
            color: #ec2072;
            text-decoration: underline;
            cursor: pointer;
            padding: 0;
            font-size: 11px;
            font-family: inherit;
          }
        }
      }

      &.step-completed {
        .step-marker .step-circle {
          background: #f78e26;
          box-shadow: 0 0 0 2px #f78e26;
        }
      }

      &.step-partial {
        .step-marker .step-circle {
          background: #f05253;
          box-shadow: 0 0 0 2px #f05253;
        }

        .step-content .step-title {
          color: #f05253;
        }
      }

      &.step-final {
        .step-marker .step-circle {
          background: #7f1a54;
          box-shadow: 0 0 0 2px #7f1a54;
        }

        .step-content .step-title {
          color: #7f1a54;
        }
      }
    }
  }

  .timeline-navigation {
    margin-top: 12px;
    display: flex;
    justify-content: center;
    gap: 24px;

    .timeline-nav-btn {
      border: none;
      background: transparent;
      color: #7f1a54;
      padding: 0;
      min-width: 24px;
      min-height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.2s ease;

      .md-icon {
        font-size: 24px !important;
        width: 24px;
        height: 24px;
      }

      &:hover:not(:disabled) {
        color: #9e1e68;
      }

      &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
      }
    }
  }
}

@media (max-width: 768px) {
  .microbiology-timeline {
    margin-top: 72px;

    .timeline-steps .timeline-step {
      flex-basis: 160px;
      min-width: 160px;
    }
  }
}
</style>
